import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaRegBuilding,
  FaUserShield,
  FaHistory,
  FaCheckCircle,
  FaRegCreditCard,
} from "react-icons/fa";
import { SlCalender } from "react-icons/sl";
import { IoIosBusiness } from "react-icons/io";
import { IoPeopleSharp } from "react-icons/io5";
import { CiSettings } from "react-icons/ci";
import { MdOutlineSecurity } from "react-icons/md";
import { MdOutlineManageAccounts } from "react-icons/md";
import { TbReportSearch } from "react-icons/tb";
import { CiViewList } from "react-icons/ci";
import { IoIosCalculator } from "react-icons/io";
import { GrPlan } from "react-icons/gr";
import { FiSearch } from "react-icons/fi";
import { FaUserTie } from "react-icons/fa6";
import { RiLogoutBoxLine } from "react-icons/ri";
import { useSettings } from "../context/SettingsContext";
import { useCompany } from "../context/CompanyContext";
import { roleFlagsFromRoles } from "../utilities/userCompanyRoles";
import api from "../api/axios";
import { FaUndoAlt } from "react-icons/fa";

const ChevronDown = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const LeftSideBar = ({ isOpen, user, collapsed }) => {
  const navigate = useNavigate();
  const { globalSettings } = useSettings();
  const {
    user: currentUser,
    companiesWithRoles,
    currentCompanyId,
    setCurrentCompanyId,
  } = useCompany();
  const [canApprove, setCanApprove] = useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = useState(currentCompanyId);
  const [pendingRefunds, setPendingRefunds] = useState(0);

  useEffect(() => {
    setExpandedCompanyId(currentCompanyId);
  }, [currentCompanyId]);

  useEffect(() => {
    const fetchApproverStatus = async () => {
      try {
        const res = await api.get("/api/my-approver-status");
        setCanApprove(res.data.can_approve);
      } catch (err) {
        console.error("Failed to fetch approver status", err);
      }
    };
    fetchApproverStatus();
  }, [currentUser]);

  // Refund requests this Superadmin can approve in the selected company, shown as a badge. The API counts
  // only pending requests made by someone else (nobody approves their own), so the badge matches the page.
  const selectedCompany = companiesWithRoles.find(
    (c) => c.company.id === currentCompanyId
  );
  const isSuperadmin = roleFlagsFromRoles(selectedCompany?.roles).isSuperadmin;
  useEffect(() => {
    if (!isSuperadmin || !currentCompanyId) {
      setPendingRefunds(0);
      return;
    }
    api
      .get("/api/refund-requests", { params: { status: "pending", per_page: 1 } })
      .then((res) => setPendingRefunds(res.data?.approvable_count || 0))
      .catch(() => setPendingRefunds(0));
  }, [isSuperadmin, currentCompanyId, currentUser]);

  const getLogo = () => {
    if (globalSettings?.logo) return globalSettings.logo;
    return "/assets/images/greenland.png";
  };

  const onLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("last_company_id");
    navigate("/", { replace: true });
    window.location.reload();
  };

  const go = (companyId, path) => {
    setCurrentCompanyId(companyId);
    navigate(path);
  };

  const renderCompanyMenu = (entry) => {
    const companyId = entry.company.id;
    const flags = roleFlagsFromRoles(entry.roles);
    const open = expandedCompanyId === companyId;

    return (
      <div key={companyId} className="company-menu-group mb-2">
        <div
          className="menu-item company-menu-header"
          style={{
            fontWeight: 600,
            background:
              currentCompanyId === companyId ? "rgba(79,70,229,0.08)" : undefined,
          }}
          onClick={() => {
            setExpandedCompanyId(open ? null : companyId);
            setCurrentCompanyId(companyId);
          }}
        >
          <IoIosBusiness />
          <span style={{ flex: 1 }}>{entry.company.name}</span>
          <ChevronDown open={open} />
        </div>

        {open && (
          <div className="company-menu-items ps-2">
            {/* One shared dashboard for Chairman, MD, Executive Director and Investor; the API decides the cards. */}
            {flags.hasLeadershipDashboard && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/leadership")}
              >
                <FaUserShield /> Leadership Dashboard
              </div>
            )}
            {flags.isFinanceDirector && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/finance-director")}
              >
                <FaUserShield /> Finance Director
              </div>
            )}

            {flags.isMarketingOfficer && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/marketing")}
              >
                <FaUserShield /> Marketing Officer
              </div>
            )}

            {flags.isGeneralUser && !flags.isManager && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/general-user")}
              >
                <IoPeopleSharp /> My Dashboard
              </div>
            )}

            {/* Each link follows the backend rule for its page (see backend/routes/api.php). */}
            {flags.canViewReports && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/dashboard")}
              >
                Dashboards
              </div>
            )}
            {flags.canCreateSales && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/dashboard/make-sale")}
              >
                <FaRegBuilding /> Make a Sale
              </div>
            )}
            {flags.isAdmin && (
              <>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/user-roles")}
                >
                  <FaUserTie /> User Role
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/property-commissions")}
                >
                  <CiSettings /> Property Commissions
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/unity-commissions")}
                >
                  <CiViewList /> Unity Commissions
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/manage-roles")}
                >
                  <FaUserShield /> Manage Role
                </div>
              </>
            )}
            {flags.canApproveBookings && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/manager/bookings")}
              >
                <CiViewList /> Booking Users List
              </div>
            )}
            {flags.canManageFinance && (
              <>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/late-fees")}
                >
                  <IoIosCalculator /> Late Fees Calculate
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/installments")}
                >
                  <GrPlan /> Installments Plan
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/installments-search")}
                >
                  <FiSearch /> Installments Search
                </div>
              </>
            )}
            {flags.isAdmin && (
              <>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/vendor-management")}
                >
                  <MdOutlineManageAccounts /> Vendor Management
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/security-management")}
                >
                  <MdOutlineSecurity /> Security Management
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/system-settings")}
                >
                  <CiSettings /> System Settings
                </div>
              </>
            )}

            {flags.canViewReports ? (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/reports")}
              >
                <TbReportSearch /> Reports
              </div>
            ) : (
              /* Finance Director: the /reports hub lists only the pending and overdue payment reports. */
              flags.canViewPaymentReports && (
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/reports")}
                >
                  <TbReportSearch /> Payment Reports
                </div>
              )
            )}
            {flags.canRequestRefunds && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/refund-requests")}
              >
                <FaUndoAlt /> Refund Requests
                {flags.isSuperadmin &&
                  companyId === currentCompanyId &&
                  pendingRefunds > 0 && (
                    <span className="badge bg-warning text-dark ms-2">
                      {pendingRefunds}
                    </span>
                  )}
              </div>
            )}
            {flags.isManager && (
              <div
                className="menu-item"
                onClick={() => go(companyId, "/properties")}
              >
                <IoIosBusiness /> Properties
              </div>
            )}

            {(flags.isApprover || canApprove) && (
              <>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/approver-dashboard")}
                >
                  <FaCheckCircle /> Approver Dashboard
                </div>
                <div
                  className="menu-item"
                  onClick={() => go(companyId, "/pending-users")}
                >
                  Pending Users
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebar-header" onClick={() => navigate("/")}>
        <img src={getLogo()} alt="logo" className="logo-img" />
      </div>

      <div className="sidebar-menu">
        <div
          className="menu-item"
          onClick={() => navigate("/profile")}
        >
          <IoPeopleSharp /> Profile
        </div>
        {/* Personal group: every signed-in user, whatever their staff roles, keeps their own customer
            pages. Approval replaces the User role, so these cannot depend on holding it. */}
        <div
          className="text-muted text-uppercase px-3 pt-2"
          style={{ fontSize: "0.7rem", letterSpacing: "0.05em" }}
        >
          Personal
        </div>
        <div
          className="menu-item"
          onClick={() => navigate("/properties_booked")}
        >
          <SlCalender /> Booking History
        </div>
        <div
          className="menu-item"
          onClick={() => navigate("/my-payments")}
        >
          <FaRegCreditCard /> My Payments
        </div>
        <div
          className="menu-item"
          onClick={() => navigate("/properties_available")}
        >
          <FaRegBuilding /> Available Properties
        </div>
        <div
          className="menu-item"
          onClick={() => navigate("/role_selection")}
        >
          <FaUserShield /> Role Selection
        </div>
        <div
          className="menu-item"
          onClick={() => navigate("/role-requests")}
        >
          <FaHistory /> Request History
        </div>

        {companiesWithRoles.length === 0 && (
          <div className="menu-item text-muted">
            No company assigned yet
          </div>
        )}

        {companiesWithRoles.map(renderCompanyMenu)}
      </div>
      <hr />
      <button onClick={onLogout} className="logout-btn">
        <RiLogoutBoxLine />
        <span>Log Out</span>
      </button>
    </aside>
  );
};

export default LeftSideBar;
